import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription } from 'rxjs';
import { UserService } from '../../../services/user.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Router, ActivatedRoute } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { AlertsService } from '../../../services/alerts.service';
import { MatBadgeModule } from '@angular/material/badge';
import { AlertsMenu } from '../alerts-menu/alerts-menu';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ProfileService } from '../../../services/profile.service';
import { HasPermissionDirective } from '../../../security/has-permission.directive';
import { USER_PERMISSIONS } from '../../../constants/permissions';

/** Supported search destination routes. 'all' routes to the unified search results page. */
type SearchTarget = 'messages' | 'networking' | 'posts' | 'all';

interface SearchOption {
  label: string;
  target: SearchTarget;
}

@Component({
  selector: 'app-student-nav',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatMenuModule,
    MatDividerModule,
    MatBadgeModule,
    HasPermissionDirective,
    AlertsMenu,
    CdkOverlayOrigin,
  ],
  templateUrl: './student-nav.html',
  styleUrl: './student-nav.css',
})
export class StudentNav implements OnInit, OnDestroy {
  /** Permission constants exposed to the template. */
  P = USER_PERMISSIONS;

  /** Whether the avatar dropdown menu is currently open. */
  dropdownOpen = false;

  /** URL of the student's avatar snapshot, or null if none saved yet. */
  avatarUrl: string | null = null;

  /** Cache-bust token updated after each new snapshot save. */
  avatarTimestamp = Date.now();

  /** Global click listener used to close the avatar dropdown when clicking outside. */
  private clickListener!: (e: MouseEvent) => void;

  /** Subscription to the userUpdated$ stream for triggering change detection. */
  private subs = new Subscription();

  constructor(
    private userService: UserService,

    private profileService: ProfileService,

    /** Used to manually trigger change detection after async user updates. */
    private cdr: ChangeDetectorRef,
    private router: Router,
    /** Service for managing alerts. */
    public alertsService: AlertsService,
    private route: ActivatedRoute,
  ) {}

  /**
   * Registers a global click listener to close the avatar dropdown when
   * clicking outside, and subscribes to user updates to keep the avatar in sync.
   * Fetches the current user if not already loaded.
   * @returns {void}
   */
  ngOnInit(): void {
    this.clickListener = (e: MouseEvent) => {
      const wrap = document.querySelector('.app-nav-avatar-wrap');
      if (wrap && !wrap.contains(e.target as Node)) this.dropdownOpen = false;
    };
    document.addEventListener('click', this.clickListener);

    this.subs.add(this.userService.userUpdated$.subscribe(() => this.cdr.markForCheck()));

    // React to avatar saves from the profile page
    this.subs.add(
      this.profileService.profileUpdated$.subscribe((profile) => {
        this.avatarUrl = profile.profile_picture_path
          ? `${profile.profile_picture_path}?t=${Date.now()}`
          : null;
        this.cdr.markForCheck();
      }),
    );

    // Load the profile picture on init
    const user = this.userService.currentUser;
    if (user) {
      this.loadAvatarUrl(user.id);
    } else {
      this.subs.add(
        this.userService.getCurrentUser().subscribe((user) => this.loadAvatarUrl(user.id)),
      );
    }

    //pre-fill search bar with query
    const q = this.route.snapshot.queryParamMap.get('q') ?? '';
    if (q) {
      this.searchControl.setValue(q);
      this.lastTypedquery = q;
    }
  }

  /** Loads the authenticated user's profile picture into `avatarUrl`. */
  private loadAvatarUrl(_userId: number): void {
    this.subs.add(
      this.profileService.getMyProfile().subscribe((res) => {
        this.avatarUrl = res.profile?.profile_picture_path
          ? `${res.profile.profile_picture_path}?t=${Date.now()}`
          : null;
        this.cdr.markForCheck();
      }),
    );
  }

  /**
   * Cleans up the global click listener and user update subscription.
   * @returns {void}
   */
  ngOnDestroy(): void {
    document.removeEventListener('click', this.clickListener);
    this.subs.unsubscribe();
  }

  /**
   * Returns the current user's uppercase initials, falling back to '?'.
   * @returns {string}
   */
  get initials(): string {
    const user = this.userService.currentUser;
    if (!user) return '?';
    const f = (user.preferredName || user.firstName)?.[0] ?? '';
    const l = user.lastName?.[0] ?? '';
    return (f + l).toUpperCase() || '?';
  }

  /**
   * Toggles the avatar dropdown open/closed.
   * @returns {void}
   */
  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  // ─── Search Bar ───────────────────────────────────────────────────────────

  /** Form control bound to the search input field. */
  searchControl = new FormControl('', { nonNullable: true });

  /** Current set of autocomplete options shown beneath the search input. */
  searchOptions: SearchOption[] = [];

  /**
   * Tracks the last typed query separately from `searchControl.value`
   * because the autocomplete selection overwrites the input value
   * before `onOptionSelected` can read it.
   */
  lastTypedquery = '';

  /**
   * Rebuilds the autocomplete options on each keystroke. Clears options when input is empty.
   * @returns {void}
   */
  onInput(): void {
    this.lastTypedquery = this.searchControl.value.trim();

    if (!this.lastTypedquery) {
      this.searchOptions = [];
      return;
    }

    this.searchOptions = [
      {
        label: `Search "${this.lastTypedquery}" in Posts`,
        target: 'posts',
      },
      {
        label: `Search "${this.lastTypedquery}" in Networking`,
        target: 'networking',
      },
      {
        label: `Search "${this.lastTypedquery}" in Messages`,
        target: 'messages',
      },
      {
        label: `See all results for "${this.lastTypedquery}"`,
        target: 'all',
      },
    ];
  }

  /**
   * Handles autocomplete option selection. Navigates to the target route
   * with the query as a `q` param — a specific category, or the unified
   * search results page for 'all'.
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event.
   * @returns {void}
   */
  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value;
    const query = this.lastTypedquery;

    if (!query) return;

    if (option === 'posts') {
      this.router.navigate(['/students'], { queryParams: { q: query } });
    } else if (option === 'networking') {
      this.router.navigate(['/network'], { queryParams: { q: query } });
    } else if (option === 'messages') {
      this.router.navigate(['/messages'], { queryParams: { q: query } });
    } else if (option === 'all') {
      this.router.navigate(['/search'], { queryParams: { q: query } });
    }

    setTimeout(() => {
      this.searchControl.setValue(query);
      this.searchOptions = [];
    }, 0);
  }

  /**
   * Sends the current query to the unified search results page, regardless
   * of which page the search bar is currently on.
   * @returns {void}
   */
  onEnter(): void {
    const query = this.searchControl.value.trim();

    this.router.navigate(['/search'], { queryParams: { q: query || null } });
    this.searchOptions = [];
  }

  /**
   * Logs out user
   * @returns {void}
   */
  logout(): void {
    // your logout logic here
    console.log('Logging out...');
    window.location.href = '/logout';
  }
}
